import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  PropertyAnalysisState, 
  PropertyAnalysisResult,
  PropertyAnalysisData,
  AnalysisToolType,
  PropertySource,
  AnalysisStatus,
  AnalysisProgress,
  PropertyAnalysisError
} from '../types/property';
import { propertyService } from '../services/PropertyService';
import { v4 as uuidv4 } from 'uuid';

// Initial state
const initialState: PropertyAnalysisState = {
  activeAnalysis: null,
  analysisHistory: [],
  exportSettings: {
    format: 'pdf',
    sections: ['property', 'pricing', 'cma', 'neighborhood', 'negotiation', 'floorPlan', 'listingDescription', 'clientConsultation'],
  },
  ui: {
    progressModalOpen: false,
    resultsTabOpen: null,
    exportModalOpen: false,
  },
};

// Async thunk for property analysis
export const analyzeProperty = createAsyncThunk(
  'propertyAnalysis/analyzeProperty',
  async (
    {
      url,
      source,
      selectedTools,
      analysisId,
    }: {
      url: string;
      source: PropertySource;
      selectedTools: AnalysisToolType[];
      analysisId?: string;
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const id = analysisId || uuidv4();
      
      // Initialize analysis
      dispatch(initializeAnalysis({
        id,
        url,
        source,
        selectedTools,
      }));

      // Start progress tracking
      dispatch(updateAnalysisProgress({
        toolName: 'overall',
        status: 'in_progress',
        progress: 0,
      }));

      // Call the analysis service
      const response = await propertyService.analyzeProperty(
        url,
        source,
        selectedTools,
        (progress) => {
          // Update progress for individual tools
          Object.entries(progress).forEach(([toolName, progressValue]) => {
            dispatch(updateAnalysisProgress({
              toolName,
              status: 'in_progress',
              progress: progressValue,
            }));
          });
        }
      );

      return {
        id,
        response,
      };
    } catch (error: any) {
      console.error('Property analysis failed:', error);
      return rejectWithValue({
        message: error.message || 'Analysis failed',
        code: error.code || 'ANALYSIS_ERROR',
        details: error.details,
      });
    }
  }
);

// Async thunk for analyzing all tools
export const analyzeAllTools = createAsyncThunk(
  'propertyAnalysis/analyzeAllTools',
  async (
    {
      url,
      source,
      analysisId,
    }: {
      url: string;
      source: PropertySource;
      analysisId?: string;
    },
    { dispatch }
  ) => {
    const allTools: AnalysisToolType[] = [
      'propertyParser',
      'cma',
      'neighborhood',
      'pricing',
      'negotiation',
      'floorPlan',
      'listingDescription',
      'clientConsultation',
      'leadQualification',
      'transactionManagement',
      'preTourPrep',
      'clientMatch',
      'buyerSentiment',
      'agentOrchestrator',
    ];

    return dispatch(analyzeProperty({
      url,
      source,
      selectedTools: allTools,
      analysisId,
    }));
  }
);

// Property analysis slice
const propertyAnalysisSlice = createSlice({
  name: 'propertyAnalysis',
  initialState,
  reducers: {
    // Initialize a new analysis
    initializeAnalysis: (state, action: PayloadAction<{
      id: string;
      url: string;
      source: PropertySource;
      selectedTools: AnalysisToolType[];
    }>) => {
      const { id, url, source, selectedTools } = action.payload;
      
      state.activeAnalysis = {
        id,
        url,
        source,
        selectedTools,
        status: 'pending',
        progress: {},
        startTime: Date.now(),
        estimatedCompletion: Date.now() + (120 * 1000), // 2 minutes
      };

      // Initialize progress for each selected tool
      selectedTools.forEach(tool => {
        state.activeAnalysis!.progress[tool] = {
          status: 'pending',
          progress: 0,
        };
      });

      // Initialize overall progress
      state.activeAnalysis!.progress['overall'] = {
        status: 'pending',
        progress: 0,
      };

      // Open progress modal
      state.ui.progressModalOpen = true;
    },

    // Update analysis progress
    updateAnalysisProgress: (state, action: PayloadAction<{
      toolName: string;
      status: AnalysisStatus;
      progress: number;
      estimatedTime?: number;
      error?: string;
    }>) => {
      if (!state.activeAnalysis) return;

      const { toolName, status, progress, estimatedTime, error } = action.payload;
      
      state.activeAnalysis.progress[toolName] = {
        status,
        progress,
        estimatedTime,
        error,
      };

      // Update overall progress based on individual tool progress
      if (toolName !== 'overall') {
        const tools = state.activeAnalysis.selectedTools;
        const totalProgress = tools.reduce((sum, tool) => {
          return sum + (state.activeAnalysis!.progress[tool]?.progress || 0);
        }, 0);
        const averageProgress = totalProgress / tools.length;

        state.activeAnalysis.progress['overall'] = {
          status: averageProgress >= 100 ? 'completed' : 'in_progress',
          progress: averageProgress,
        };
      }

      // Update overall analysis status
      if (status === 'error' && toolName !== 'overall') {
        state.activeAnalysis.status = 'error';
        state.activeAnalysis.error = error;
      } else if (toolName === 'overall' && status === 'completed') {
        state.activeAnalysis.status = 'completed';
      } else if (state.activeAnalysis.status === 'pending') {
        state.activeAnalysis.status = 'in_progress';
      }
    },

    // Cancel active analysis
    cancelAnalysis: (state) => {
      if (state.activeAnalysis) {
        propertyService.cancelAnalysis(state.activeAnalysis.id);
        state.activeAnalysis.status = 'error';
        state.activeAnalysis.error = 'Analysis cancelled by user';
      }
    },

    // Clear active analysis
    clearActiveAnalysis: (state) => {
      state.activeAnalysis = null;
      state.ui.progressModalOpen = false;
      state.ui.resultsTabOpen = null;
    },

    // Add analysis to history
    addToHistory: (state, action: PayloadAction<PropertyAnalysisResult>) => {
      const analysis = action.payload;
      
      // Remove any existing analysis with the same ID
      state.analysisHistory = state.analysisHistory.filter(
        item => item.id !== analysis.id
      );
      
      // Add to beginning of history
      state.analysisHistory.unshift(analysis);
      
      // Limit history to 50 items
      if (state.analysisHistory.length > 50) {
        state.analysisHistory = state.analysisHistory.slice(0, 50);
      }
    },

    // Remove analysis from history
    removeFromHistory: (state, action: PayloadAction<string>) => {
      state.analysisHistory = state.analysisHistory.filter(
        item => item.id !== action.payload
      );
    },

    // Clear analysis history
    clearHistory: (state) => {
      state.analysisHistory = [];
    },

    // Update export settings
    updateExportSettings: (state, action: PayloadAction<Partial<PropertyAnalysisState['exportSettings']>>) => {
      state.exportSettings = {
        ...state.exportSettings,
        ...action.payload,
      };
    },

    // UI state management
    setProgressModalOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.progressModalOpen = action.payload;
    },

    setResultsTabOpen: (state, action: PayloadAction<string | null>) => {
      state.ui.resultsTabOpen = action.payload;
    },

    setExportModalOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.exportModalOpen = action.payload;
    },

    // Reset UI state
    resetUIState: (state) => {
      state.ui = {
        progressModalOpen: false,
        resultsTabOpen: null,
        exportModalOpen: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle successful analysis
      .addCase(analyzeProperty.fulfilled, (state, action) => {
        const { id, response } = action.payload;
        
        if (state.activeAnalysis && state.activeAnalysis.id === id) {
          // Mark analysis as completed
          state.activeAnalysis.status = 'completed';
          // Handle double-wrapped response structure
          const actualData = (response.data as any).data || response.data;
          state.activeAnalysis.results = actualData;
          
          // Update all tool progress to completed
          Object.keys(state.activeAnalysis.progress).forEach(toolName => {
            if (state.activeAnalysis!.progress[toolName]) {
              state.activeAnalysis!.progress[toolName].status = 'completed';
              state.activeAnalysis!.progress[toolName].progress = 100;
            }
          });

          // Add to history
          const historyItem: PropertyAnalysisResult = {
            id,
            url: state.activeAnalysis.url,
            source: state.activeAnalysis.source,
            selectedTools: state.activeAnalysis.selectedTools,
            results: actualData, // Use the unwrapped data
            timestamp: response.timestamp,
          };
          
          propertyAnalysisSlice.caseReducers.addToHistory(state, {
            type: 'propertyAnalysis/addToHistory',
            payload: historyItem,
          });

          // Open results tab
          state.ui.resultsTabOpen = 'property';
        }
      })
      // Handle analysis failure
      .addCase(analyzeProperty.rejected, (state, action) => {
        if (state.activeAnalysis) {
          state.activeAnalysis.status = 'error';
          const errorMessage = (action.payload as any)?.message || action.error?.message || 'Analysis failed';
          state.activeAnalysis.error = errorMessage;
          
          // Mark all tools as error
          Object.keys(state.activeAnalysis.progress).forEach(toolName => {
            if (state.activeAnalysis!.progress[toolName]) {
              state.activeAnalysis!.progress[toolName].status = 'error';
              state.activeAnalysis!.progress[toolName].error = errorMessage;
            }
          });
        }
      })
      // Handle analysis start
      .addCase(analyzeProperty.pending, (state) => {
        if (state.activeAnalysis) {
          state.activeAnalysis.status = 'in_progress';
        }
      });
  },
});

// Export actions
export const {
  initializeAnalysis,
  updateAnalysisProgress,
  cancelAnalysis,
  clearActiveAnalysis,
  addToHistory,
  removeFromHistory,
  clearHistory,
  updateExportSettings,
  setProgressModalOpen,
  setResultsTabOpen,
  setExportModalOpen,
  resetUIState,
} = propertyAnalysisSlice.actions;

// Selectors
export const selectPropertyAnalysis = (state: { propertyAnalysis: PropertyAnalysisState }) => 
  state.propertyAnalysis;

export const selectActiveAnalysis = (state: { propertyAnalysis: PropertyAnalysisState }) => 
  state.propertyAnalysis.activeAnalysis;

export const selectAnalysisHistory = (state: { propertyAnalysis: PropertyAnalysisState }) => 
  state.propertyAnalysis.analysisHistory;

export const selectAnalysisProgress = (state: { propertyAnalysis: PropertyAnalysisState }) => 
  state.propertyAnalysis.activeAnalysis?.progress || {};

export const selectAnalysisResults = (state: { propertyAnalysis: PropertyAnalysisState }) => 
  state.propertyAnalysis.activeAnalysis?.results;

export const selectExportSettings = (state: { propertyAnalysis: PropertyAnalysisState }) => 
  state.propertyAnalysis.exportSettings;

export const selectUIState = (state: { propertyAnalysis: PropertyAnalysisState }) => 
  state.propertyAnalysis.ui;

export const selectIsAnalysisActive = (state: { propertyAnalysis: PropertyAnalysisState }) => 
  state.propertyAnalysis.activeAnalysis?.status === 'in_progress';

export const selectAnalysisById = (state: { propertyAnalysis: PropertyAnalysisState }, id: string) => 
  state.propertyAnalysis.analysisHistory.find(analysis => analysis.id === id);

// Export reducer
export default propertyAnalysisSlice.reducer;